import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, PanelLeftClose, PanelLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Sidebar } from './Sidebar';

/**
 * Layout adaptatif pour les pages publiques (Blog, FAQ, Country, etc.)
 *
 * - Si l'utilisateur est connecté → affiche la Sidebar de l'app + une topbar
 *   minimale (bouton mobile menu + collapse desktop + breadcrumb optionnel).
 *   Le `publicHeader` est masqué pour éviter la double navigation.
 *
 * - Si l'utilisateur est déconnecté → affiche le `publicHeader` (Link
 *   "Connexion" + langue) tel quel.
 *
 * Cela permet à un télépilote connecté de naviguer librement vers le Blog/FAQ/
 * pages pays sans perdre son contexte d'application.
 *
 * Props :
 *  - children : contenu principal de la page
 *  - publicHeader : élément React à afficher en haut en mode public
 *                   (caché si user connecté)
 *  - title : titre affiché dans la topbar en mode connecté (optionnel)
 */
export default function PublicPageLayout({ children, publicHeader, title }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Mode "déconnecté" → public header normal
  if (!user) {
    return <>{publicHeader}{children}</>;
  }

  // Mode "connecté" → app shell with Sidebar
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
      />

      <div className={`transition-all duration-200 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Minimal topbar — keeps the menu accessible on mobile / collapse on desktop */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-4 lg:px-8 h-14">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded hover:bg-slate-100"
                data-testid="public-mobile-menu-btn"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden lg:flex p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700"
                title={sidebarCollapsed ? 'Show menu' : 'Collapse menu'}
                data-testid="public-collapse-sidebar-btn"
              >
                {sidebarCollapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
              </button>
              {title && (
                <h1 className="text-lg font-semibold text-slate-900 font-[Barlow] truncate">{title}</h1>
              )}
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm font-medium text-sky-600 hover:text-sky-700"
              data-testid="back-to-app-btn"
            >
              ← Dashboard
            </button>
          </div>
        </header>

        <main>{children}</main>
      </div>
    </div>
  );
}
